import { Component, Input, OnInit } from '@angular/core';
import {
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from 'on-common/_services/toast.service';
import { ElectronicInvoicingEntity } from 'on-shared/_models/ElectronicInvoicingEntity';
import { CompanyService } from 'on-shared/stores/company/company.service';
import Swal from 'sweetalert2';

// SIREN: 9 digits, spaces allowed
const SIREN_PATTERN = /^(?:\s*\d){9}\s*$/;

@Component({
    selector: 'on-einvoicing-entity-modal',
    templateUrl: './einvoicing-entity-modal.component.html',
    styleUrls: ['./einvoicing-entity-modal.component.scss'],
})
export class EInvoicingEntityModalComponent implements OnInit {
    @Input() entity: ElectronicInvoicingEntity;

    entityForm: UntypedFormGroup;

    constructor(
        public activeModal: NgbActiveModal,
        private fb: UntypedFormBuilder,
        private companyService: CompanyService,
        private toastService: ToastService,
        private translate: TranslateService,
    ) {}

    get isNew(): boolean {
        return !this.entity || !this.entity.Id;
    }

    ngOnInit() {
        this.entityForm = this.fb.group({
            Name: [this.entity?.Name ?? '', Validators.required],
            Siren: [
                this.entity?.Siren ?? '',
                [Validators.required, Validators.pattern(SIREN_PATTERN)],
            ],
            ElectronicInvoicingAddress: [
                this.entity?.ElectronicInvoicingAddress ?? '',
                Validators.required,
            ],
        });
    }

    createEntity(): void {
        const entityToCreate = this.buildEntityObject();

        this.companyService
            .AddElectronicInvoicingEntity(entityToCreate)
            .subscribe(
                (response) => {
                    if (response.IsSuccess) {
                        this.toastService.success(
                            this.translate.instant(
                                'company.einvoicing.created-success',
                            ),
                        );
                        this.activeModal.close(true);
                    } else {
                        this.toastService.error(
                            response.Message ||
                                this.translate.instant(
                                    'company.einvoicing.create-error',
                                ),
                        );
                    }
                },
                () =>
                    this.toastService.error(
                        this.translate.instant(
                            'company.einvoicing.create-error',
                        ),
                    ),
            );
    }

    updateEntity(): void {
        const entityToUpdate = this.buildEntityObject();

        this.companyService
            .UpdateElectronicInvoicingEntity(entityToUpdate)
            .subscribe(
                (response) => {
                    if (response.IsSuccess) {
                        this.toastService.success(
                            this.translate.instant(
                                'company.einvoicing.updated-success',
                            ),
                        );
                        this.activeModal.close(true);
                    } else {
                        this.toastService.error(
                            response.Message ||
                                this.translate.instant(
                                    'company.einvoicing.update-error',
                                ),
                        );
                    }
                },
                () =>
                    this.toastService.error(
                        this.translate.instant(
                            'company.einvoicing.update-error',
                        ),
                    ),
            );
    }

    deleteEntity(): void {
        Swal.fire({
            title: this.translate.instant(
                'company.einvoicing.delete-confirm-title',
            ),
            text: this.translate.instant(
                'company.einvoicing.delete-confirm-text',
                { name: this.entity.Name },
            ),
            icon: 'warning',
            showConfirmButton: true,
            showCancelButton: true,
        }).then((result) => {
            if (result.value) {
                this.companyService
                    .DeleteElectronicInvoicingEntity(this.entity.Id)
                    .subscribe(
                        (response) => {
                            if (response.IsSuccess) {
                                this.toastService.success(
                                    this.translate.instant(
                                        'company.einvoicing.deleted-success',
                                    ),
                                );
                                this.activeModal.close(true);
                            } else {
                                this.toastService.error(
                                    response.Message ||
                                        this.translate.instant(
                                            'company.einvoicing.delete-error',
                                        ),
                                );
                            }
                        },
                        () =>
                            this.toastService.error(
                                this.translate.instant(
                                    'company.einvoicing.delete-error',
                                ),
                            ),
                    );
            }
        });
    }

    copyQrCodeContent(): void {
        navigator.clipboard.writeText(this.entity.QrCodeContent).then(() => {
            this.toastService.success(
                this.translate.instant('company.einvoicing.qr-copied'),
            );
        });
    }

    private buildEntityObject(): ElectronicInvoicingEntity {
        const entity = new ElectronicInvoicingEntity(this.entity);
        entity.Name = this.entityForm.value.Name;
        entity.Siren = this.entityForm.value.Siren;
        entity.ElectronicInvoicingAddress =
            this.entityForm.value.ElectronicInvoicingAddress;
        return entity;
    }
}
